/**
 * Шапка «Сегодня»: обращение по имени, когда ученик был в прошлый раз и
 * успевает ли он к экзамену.
 *
 * **Почему это не прогноз балла.** Решение 15 запрещает на дневном экране цель
 * по результату: показанная каждый день, она повышает тревогу там, где
 * стратегия ещё не освоена. Здесь стоит цель освоения — сколько навыков карты
 * будет пройдено к июню при нынешнем темпе. Балла нет ни числом, ни намёком;
 * он по-прежнему ждёт калибровки фазы 5.
 *
 * **Почему строки не повторяют полосы.** Под шапкой висят три полосы, и они уже
 * говорят: дни в окне, требуемый темп, пройдено из 226. Повторить это словами
 * значило бы занять верх экрана числами, которые ниже нарисованы точнее.
 * Шапка отвечает на два вопроса, которых нет нигде: когда он был в прошлый раз
 * и чем кончится нынешний темп.
 *
 * Тон: факт без оценки человека. «Не был 4 дня» пишется без «жаль» и без
 * «наконец-то», а прогноз — числом, а не словом «плохо».
 */
import { daysUntil, plural } from "./dates";
import type { ProgressSnapshot } from "../api";

export type PartOfDay = "night" | "morning" | "day" | "evening";

/**
 * Часы берутся с часов ученика, а не из его часового пояса в базе. Пояс в
 * `learners.timeZone` решает, где граница суток для набора; «доброе утро»
 * решают часы того, кто смотрит на экран, и разойтись эти два ответа могут
 * только у ученика в поездке.
 */
export function partOfDay(hour: number): PartOfDay {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 18) return "day";
  if (hour >= 18 && hour < 23) return "evening";
  return "night";
}

const HELLO: Record<PartOfDay, string> = {
  morning: "Доброе утро",
  day: "Добрый день",
  evening: "Добрый вечер",
  night: "Доброй ночи",
};

/**
 * «Добрый вечер, Тимофей».
 *
 * Пустое имя — здороваемся без имени. Подставить «ученик» значило бы обратиться
 * к человеку словом из базы данных, а это хуже, чем не обратиться никак.
 */
export function greet(name: string, hour: number): string {
  const hello = HELLO[partOfDay(hour)];
  const trimmed = name.trim();
  return trimmed === "" ? hello : `${hello}, ${trimmed}`;
}

/**
 * Когда ученик был в прошлый раз. `null` — прошлого раза система не видит:
 * либо это первый день, либо он не заходил дольше окна.
 *
 * Считается по `snapshot.days`, где лежат дни с попытками за последние три
 * недели. Своего запроса под это не заводится: снимок экран и так берёт на
 * открытии, а вторая подписка на главной — то, от чего предостерегает
 * инвариант 4.
 */
export function visitLine(
  days: readonly { date: string; tasks: number }[],
  today: string,
): string | null {
  const prev = days.find((d) => d.date !== today && d.tasks > 0);
  if (prev === undefined) return null;
  const gap = daysUntil(today, prev.date);
  if (gap <= 0) return null;
  if (gap === 1) return "Был вчера";
  if (gap === 2) return "Был позавчера";
  // Дальше трёх недель журнал снимка не видит: в него попадают попытки за
  // окно, и точное число там было бы выдумкой.
  if (gap >= 21) return "Не был больше трёх недель";
  return `Не был ${gap} ${plural(gap, "день", "дня", "дней")}`;
}

/**
 * Чем кончится нынешний темп. `null` — судить рано.
 *
 * Первая неделя не судится вовсе, и по той же причине, по которой недельная
 * полоса молчит: окно в первый день пусто по построению, и «не успеваешь»
 * через десять секунд после начала — это ложь в первую же минуту.
 *
 * Прогноз считается по покрытому банком (`tempo.covered`), а не по всей карте:
 * темп должен мерить достижимое. Сейчас покрытие полное, и числа совпадают.
 */
export function paceLine(s: ProgressSnapshot): string | null {
  if (s.learnerDays <= 7) return null;
  const total = s.tempo.covered;
  const left = total - s.skills.passed;
  if (left <= 0) return "Карта пройдена целиком";
  if (s.tempo.actualPerWeek <= 0) {
    const d = s.tempo.recentDays;
    return `За ${d} ${plural(d, "день", "дня", "дней")} ни одного нового навыка`;
  }
  const weeksLeft = s.daysToExam / 7;
  const projected = Math.round(s.skills.passed + s.tempo.actualPerWeek * weeksLeft);
  if (projected >= total) return "Нынешний темп закрывает карту до экзамена";
  return `При нынешнем темпе к экзамену будет пройдено ${projected} из ${total}`;
}
